import { FC, ReactNode, useEffect, useState } from "react";

import { Box, ChakraProvider } from "@chakra-ui/react";
import { Userpilot } from "userpilot";
import { useCookies } from "react-cookie";

import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import { getCustomTheme } from "theme";
import { getSemanticTokens, getThemeColors } from "themes";
import { RudderStackScript } from "app-head";

import { DAYS_TO_WAIT_FOR_WITHDRAW, ETH } from "@constants/constants";
import { APPROVAL_AMOUNT } from "@constants/contract";
import { dappWalletMappings } from "@constants/dappWalletMapping";
import { dappBannerMappings } from "@constants/dappBannerMapping";
import { descriptionMappings, titleMappings } from "@constants/meta";

import Banner from "@components/Banner";
// import Fonts from "@components/Fonts";

import { trackEvent } from "@utils/firebase";
import { useSafeMode } from "dapp-sdk";

import "../styles/globals.scss";

const StaderProvider = dynamic(
  () => import("dapp-sdk").then((module) => module.StaderProvider),
  {
    ssr: false,
  }
);

const DefaultHead = () => {
  const token = process.env.NEXT_PUBLIC_TOKEN || "";
  return (
    <Head>
      <title>{titleMappings[token?.toUpperCase()]}</title>
      <meta
        name="description"
        content={descriptionMappings[token?.toUpperCase()]}
      />
      <link rel="icon" href="favicon.ico" />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <RudderStackScript />
    </Head>
  );
};

const initialState = {
  text: null,
  link: null,
  show: false,
  ctaText: null,
  isClosable: true,
  type: "",
};

const cookieName = `promo_banner_disabled_${process.env.NEXT_PUBLIC_TOKEN}`;
const VID = "vid";

Userpilot.initialize(process.env.NEXT_PUBLIC_USERPILOT_KEY || "");

const App: FC<AppProps> = ({ Component, pageProps }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [bannerDetails, setBannerDetails] = useState(initialState);
  const [cookies, setCookie] = useCookies([cookieName]);
  const [isSafeMode, setIsSafeMode] = useState(false);

  const router = useRouter();

  const {
    query: { referralId },
  } = router;

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (router.query[VID] && !cookies[VID]) {
      setCookie(VID, router.query[VID]);
    }
  }, [router.query, cookies, setCookie]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (url === router.asPath) {
        Userpilot.reload();
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    if (
      isSafeMode &&
      process.env.NEXT_PUBLIC_TOKEN === ETH &&
      dappBannerMappings[process.env.NEXT_PUBLIC_TOKEN]
    ) {
      const bannerData = dappBannerMappings[process.env.NEXT_PUBLIC_TOKEN];
      setBannerDetails({
        ...bannerData,
        text: bannerData?.textSafeMode || bannerData?.text,
      });
    }
  }, [isSafeMode]);

  useEffect(() => {
    if (cookies[cookieName]) {
      return;
    }
    if (
      process.env.NEXT_PUBLIC_TOKEN &&
      dappBannerMappings[process.env.NEXT_PUBLIC_TOKEN]
    ) {
      setBannerDetails(dappBannerMappings[process.env.NEXT_PUBLIC_TOKEN]);
    }
  }, [isLoaded]);

  const closeBanner = () => {
    setCookie(cookieName, true, {
      maxAge: 1209600,
    });
    setBannerDetails((prev) => ({ ...prev, show: false }));
  };

  const isBannerNotAllowed = ((Component as any).isHomePage ||
    (Component as any).isNodePage) as boolean;

  const removeReferralQueryParam = () => {
    const { pathname, query } = router;
    delete router.query.referralId;
    router.replace({ pathname, query }, undefined, { shallow: true });
  };

  return (
    <>
      <DefaultHead />
      <ChakraProvider
        theme={getCustomTheme(
          getThemeColors(process.env.NEXT_PUBLIC_TOKEN),
          getSemanticTokens(process.env.NEXT_PUBLIC_TOKEN)
        )}
      >
        <StaderProvider
          providerKey={
            process.env.NEXT_PUBLIC_NETWORK === "mainnnet"
              ? process.env.NEXT_PUBLIC_ALCHEMY_KEY_MAINNET
              : process.env.NEXT_PUBLIC_ALCHEMY_KEY_TESTNET
          }
          network={process.env.NEXT_PUBLIC_NETWORK || "0"}
          token={process.env.NEXT_PUBLIC_TOKEN || ""}
          daysForWithdraw={DAYS_TO_WAIT_FOR_WITHDRAW}
          logEvent={trackEvent}
          approvalAmount={APPROVAL_AMOUNT}
          theme={getCustomTheme(
            getThemeColors(process.env.NEXT_PUBLIC_TOKEN),
            getSemanticTokens(process.env.NEXT_PUBLIC_TOKEN)
          )}
          wallets={
            dappWalletMappings[process.env.NEXT_PUBLIC_TOKEN || ""] || [
              "matamask",
              "coinhub",
              "walletconnect",
            ]
          }
          referralId={referralId as unknown as string}
          refrerralCallBack={removeReferralQueryParam}
        >
          {/* <Fonts /> */}
          {!isBannerNotAllowed && (
            <>
              {bannerDetails.show && bannerDetails.text && (
                <Banner
                  text={bannerDetails.text || ""}
                  link={bannerDetails.link || ""}
                  ctaText={bannerDetails.ctaText || ""}
                  isClosable={bannerDetails.isClosable}
                  closeBanner={closeBanner}
                  isSafeModeActive={isSafeMode}
                  type={bannerDetails.type}
                />
              )}
            </>
          )}
          <StaderWrapper
            setIsSafeMode={(isSafeModeEnabled) =>
              setIsSafeMode(isSafeModeEnabled)
            }
            isBannerVisible={
              ((bannerDetails.show && bannerDetails.text) as boolean) &&
              !isBannerNotAllowed
            }
          >
            <Component
              {...pageProps}
              shouldDisplayBanner={bannerDetails.show}
              isSafeMode={isSafeMode}
            />
          </StaderWrapper>
        </StaderProvider>
      </ChakraProvider>
    </>
  );
};

interface StaderWrapperProps {
  children: ReactNode;
  isBannerVisible: boolean;
  setIsSafeMode: (isSafeModeEnabled: boolean) => void;
}

const StaderWrapper = ({
  children,
  isBannerVisible,
  setIsSafeMode,
}: StaderWrapperProps) => {
  const safeModeData = useSafeMode();

  useEffect(() => {
    setIsSafeMode(safeModeData.safeMode as boolean);
  }, [safeModeData, setIsSafeMode]);

  return (
    <Box mt={{ base: 0, lg: isBannerVisible ? "2rem" : 0 }}>{children}</Box>
  );
};

export default App;
